import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { datasetApi } from '../api/datasetApi';
import { workspaceApi } from '../api/workspaceApi';
import WorkspaceCard, { WORKSPACE_CARD_GRID } from '../components/workspace/WorkspaceCard';
import WorkspaceSummaryModal from '../components/workspace/WorkspaceSummaryModal';
import { useAuth } from '../context/AuthContext';
import { useWorkspaceCardActions } from '../hooks/useWorkspaceCardActions';
import { isUploadDatasetType } from '../utils/datasetDisplayUtils';
import { cn } from '../lib/utils';

const StartChecklistItem = ({ complete, isNext, isUpcoming, title, description, to, ctaLabel, actions = [] }) => (
  <div
    className={cn(
      'group relative flex items-center rounded-2xl border transition-all',
      complete
        ? 'gap-2 border-gray-100 bg-gray-50/60 px-3 py-2 dark:border-gray-800 dark:bg-gray-900/40'
        : isNext
          ? 'gap-3 border-emerald-400 bg-gradient-to-br from-emerald-50 via-white to-white p-3 shadow-md shadow-emerald-100/80 ring-2 ring-emerald-100 hover:border-emerald-500 dark:border-emerald-700 dark:from-emerald-950/30 dark:via-gray-900 dark:to-gray-900 dark:shadow-none dark:ring-emerald-900/40'
          : 'gap-3 border-dashed border-gray-200 bg-gray-50/80 p-3 opacity-60 dark:border-gray-800 dark:bg-gray-900/30'
    )}
  >
    <div
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full border text-xs font-black transition-colors',
        complete
          ? 'h-6 w-6 border-gray-200 bg-gray-100 text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500'
          : isNext
            ? 'h-10 w-10 border-emerald-600 bg-white text-emerald-700 shadow-sm dark:border-emerald-500 dark:bg-gray-950 dark:text-emerald-300'
            : 'h-10 w-10 border-gray-200 bg-gray-100 text-gray-300 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-600'
      )}
    >
      {complete ? <CheckCircle2 size={14} strokeWidth={2.5} /> : <ArrowRight size={15} strokeWidth={2.5} />}
    </div>
    <div className="min-w-0 flex-1">
      {!complete && isNext && (
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
            Next step
          </span>
        </div>
      )}
      <h3
        className={cn(
          'font-black',
          complete
            ? 'truncate text-xs text-gray-500 dark:text-gray-400'
            : isUpcoming
              ? 'text-sm text-gray-400 dark:text-gray-500'
              : 'text-sm text-gray-950 dark:text-white'
        )}
      >
        {title}
      </h3>
      {!complete && (
        <p
          className={cn(
            'mt-0.5 text-xs',
            isUpcoming ? 'text-gray-400 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400'
          )}
        >
          {description}
        </p>
      )}
      {!complete && isNext && actions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {actions.map(action => (
            <Link
              key={action.label}
              to={action.to}
              className={cn(
                'inline-flex items-center justify-center rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition-colors',
                action.recommended
                  ? 'bg-emerald-600 text-white shadow-sm shadow-emerald-200 hover:bg-emerald-700 dark:bg-emerald-500 dark:text-gray-950 dark:shadow-none dark:hover:bg-emerald-400'
                  : 'border border-gray-200 bg-white text-gray-500 hover:border-emerald-300 hover:text-emerald-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-emerald-700 dark:hover:text-emerald-300'
              )}
            >
              {action.label}
              {action.recommended && (
                <span className="ml-2 rounded-full bg-white/20 px-1.5 py-0.5 text-[8px] dark:bg-gray-950/15">
                  Recommended
                </span>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
    {complete ? (
      <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-gray-400 dark:bg-gray-800 dark:text-gray-500">
        Done
      </span>
    ) : isNext && actions.length === 0 ? (
      <Link
        to={to}
        className="hidden shrink-0 rounded-full bg-emerald-600 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-white shadow-sm group-hover:bg-emerald-700 dark:bg-emerald-500 dark:text-gray-950 dark:group-hover:bg-emerald-400 sm:inline"
      >
        {ctaLabel}
      </Link>
    ) : null}
  </div>
);

const HomePage = () => {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const {
    selectedWorkspace,
    setSelectedWorkspace,
    exportingWorkspaceId,
    canEditWorkspaceJson,
    handleDeleteWorkspace,
    handleUpdateWorkspace,
    handleExportWorkspaceJson,
    handleOpenWorkspace,
    handlePreviewWorkspace,
    getWorkspaceLocked,
  } = useWorkspaceCardActions(workspaces, setWorkspaces);

  useEffect(() => {
    document.title = 'manteo.ai | Home';
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);

    Promise.allSettled([
      workspaceApi.getWorkspaces(),
      datasetApi.getDatasets(),
    ])
      .then(results => {
        if (cancelled) return;
        const [workspaceResult, datasetResult] = results;
        const loadedWorkspaces = workspaceResult.status === 'fulfilled' ? workspaceResult.value : [];
        setWorkspaces(loadedWorkspaces);
        setDatasets(datasetResult.status === 'fulfilled' ? datasetResult.value : []);
        setLoadError([workspaceResult, datasetResult].some(result => result.status === 'rejected'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const recentWorkspaces = useMemo(
    () =>
      [...workspaces]
        .sort((a, b) => {
          const aTs = new Date(a.created_at || a.createdAt || 0).getTime();
          const bTs = new Date(b.created_at || b.createdAt || 0).getTime();
          return bTs - aTs;
        })
        .slice(0, 3),
    [workspaces]
  );
  const moreWorkspacesCount = Math.max(0, workspaces.length - recentWorkspaces.length);

  const firstName = user?.full_name?.split(' ')?.[0] || user?.email?.split('@')?.[0] || 'there';
  const hasWorkspace = workspaces.length > 0;
  const hasAskedQuestion = workspaces.some(workspace => Number(workspace.chatsCount || workspace.chats_count || 0) > 0);
  const hasOwnDataset = datasets.some(dataset => isUploadDatasetType(dataset.type));
  const firstWorkspaceId = recentWorkspaces[0]?.id || workspaces[0]?.id;

  const startChecklist = [
    {
      title: 'Create a workspace',
      description: hasWorkspace
        ? 'Your workspace is ready.'
        : 'A workspace is where you combine datasets, chat with your data, and build maps. Sample workspaces are the fastest way to begin.',
      to: '/workspaces?filter=samples',
      ctaLabel: 'Browse samples',
      actions: [
        { label: 'Browse sample workspaces', to: '/workspaces?filter=samples', recommended: true },
      ],
      complete: hasWorkspace,
    },
    {
      title: 'Ask your first question',
      description: hasAskedQuestion
        ? 'You have started analyzing.'
        : 'Open a workspace and ask something about your data in plain language.',
      to: firstWorkspaceId ? `/workspace/${firstWorkspaceId}` : '/workspaces',
      ctaLabel: 'Ask',
      complete: hasAskedQuestion,
    },
    {
      title: 'Upload your own dataset',
      description: hasOwnDataset
        ? 'Your dataset is semantically indexed and ready to add to any workspace.'
        : 'Upload a dataset of your own. Manteo will index it to allow you to ask questions in natural language.',
      to: '/sources',
      ctaLabel: 'Upload',
      actions: [
        { label: 'Go to data sources', to: '/sources', recommended: true },
      ],
      complete: hasOwnDataset,
    },
  ];
  const nextChecklistTitle = startChecklist.find(item => !item.complete)?.title;
  const showStartChecklist = !loading && startChecklist.some(item => !item.complete);

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50/50 px-8 py-8 transition-colors duration-300 dark:bg-gray-950">
      <WorkspaceSummaryModal
        workspace={selectedWorkspace}
        isOpen={Boolean(selectedWorkspace)}
        onClose={() => setSelectedWorkspace(null)}
        onOpenWorkspace={handleOpenWorkspace}
        onUpdateWorkspace={handleUpdateWorkspace}
      />
      <div className="mx-auto max-w-7xl space-y-5">
        <header className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <h1 className="text-3xl font-black uppercase tracking-tight text-gray-900 dark:text-white">Home</h1>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
              Your command center
            </p>
          </div>
        </header>

        <section className="rounded-[28px] border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 lg:p-5">
          <div className="min-w-0">
            <h1 className="max-w-5xl text-2xl font-black tracking-tight text-gray-950 dark:text-white lg:text-3xl">
              Welcome, {firstName}.{' '}
              <span className="text-emerald-600 dark:text-emerald-300">
                Turn spatial data into answers.
              </span>
            </h1>
            <p className="mt-1.5 max-w-4xl text-sm leading-6 text-gray-600 dark:text-gray-300">
              Connect data, organize workspaces, ask questions, and generate maps from one place.
            </p>
          </div>

          {showStartChecklist ? (
            <div className="mt-5 border-t border-gray-100 pt-5 dark:border-gray-800">
              <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h2 className="text-base font-black text-gray-950 dark:text-white">Start checklist</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Set up the basics before you start analyzing.</p>
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">
                  {startChecklist.filter(item => item.complete).length} / {startChecklist.length} complete
                </span>
              </div>
              <div className="grid gap-3 lg:grid-cols-3">
                {startChecklist.map(item => (
                  <StartChecklistItem
                    key={item.title}
                    isNext={item.title === nextChecklistTitle}
                    isUpcoming={!item.complete && item.title !== nextChecklistTitle}
                    {...item}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {loading || hasWorkspace ? (
            <div className="mt-5 border-t border-gray-100 pt-5 dark:border-gray-800">
              <div className="mb-4">
                <h2 className="text-base font-black text-gray-950 dark:text-white">Recent workspaces</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">Pick up the analysis you were working on.</p>
              </div>

              {loading ? (
                <div className={WORKSPACE_CARD_GRID}>
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-48 animate-pulse rounded-3xl bg-gray-100 dark:bg-gray-900" />
                  ))}
                </div>
              ) : (
                <div className={WORKSPACE_CARD_GRID}>
                  {recentWorkspaces.map(workspace => (
                    <WorkspaceCard
                      key={workspace.id}
                      {...workspace}
                      createdAt={workspace.created_at || workspace.createdAt}
                      chatsCount={workspace.chatsCount || workspace.chats_count || 0}
                      onPreview={handlePreviewWorkspace}
                      onOpenWorkspace={handleOpenWorkspace}
                      onDelete={handleDeleteWorkspace}
                      showExportJson={canEditWorkspaceJson}
                      onExportJson={handleExportWorkspaceJson}
                      exportJsonLoading={exportingWorkspaceId === workspace.id}
                      isLocked={getWorkspaceLocked(workspace)}
                    />
                  ))}
                  {moreWorkspacesCount > 0 ? (
                    <Link
                      to="/workspaces"
                      className="group flex h-full min-h-[12rem] flex-col items-center justify-center gap-2 rounded-3xl border-2 border-dashed border-emerald-200/80 bg-emerald-50/40 p-6 text-center transition-all hover:border-emerald-400 hover:bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-950/20 dark:hover:border-emerald-700 dark:hover:bg-emerald-950/35"
                    >
                      <span className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-[11px] font-black uppercase tracking-widest text-white shadow-lg shadow-emerald-600/20 transition-all group-hover:-translate-y-0.5 group-hover:bg-emerald-700 dark:bg-emerald-500 dark:text-emerald-950 dark:shadow-emerald-950/30 dark:group-hover:bg-emerald-400">
                        Browse all
                        <ArrowRight size={14} strokeWidth={2.75} />
                      </span>
                    </Link>
                  ) : null}
                </div>
              )}
            </div>
          ) : null}
        </section>

        {loadError ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
            Some homepage data could not be loaded. You can still open workspaces or data sources from the navigation.
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default HomePage;
